import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Activity, Package, Plug, Send } from "lucide-react";

import {
  Icon,
  IconCalendar,
  IconChart,
  IconChevronDown,
  IconDatabase,
  IconGlobe,
  IconGrid,
  IconSwords,
  IconHome,
  IconNetwork,
  IconPlus,
  IconSearch,
  IconSettings,
  IconSignOut,
  IconTag,
  IconTrophy,
  IconUser,
  IconZap,
} from "@/components/Icons";
import {
  SportSwitcher,
  type SwitcherSport,
} from "@/components/sidebar/SportSwitcher";
import { listMasters } from "@/lib/api/client";
import { isCurrent } from "@/lib/isCurrent";
import { masterIcon } from "@/lib/masterIcon";
import type { BindingKind } from "@/lib/api/types";
import { displayName } from "@/lib/format";
import {
  NONE,
  getServerSnapshot,
  getSnapshot,
  scopeOf,
  subscribe,
  toggle,
  type Collapsed,
} from "@/lib/sidebarPrefs";
import { SPORT_COOKIE, SPORT_COOKIE_MAX_AGE } from "@/lib/sportCookie";

import styles from "./sidebar.module.css";

export type SportNavItem = SwitcherSport;
export type MasterNavItem = {
  code: string;
  label: string;
  bindingKind: BindingKind;
};

type Props = {
  sports: SportNavItem[];
  /** Last sport worked in, from the cookie. Used on pages that are not
   * sport-scoped so the selection survives a trip to /sports or a fixture. */
  rememberedSport?: string | null;
  /** The masters of whichever sport the *first* request was for. Correct on a
   * fresh load and stale after that — see `useSportMasters`. */
  masters: MasterNavItem[];
  /** The signed-in person's name, or null when nobody is signed in. */
  signedInAs?: string | null;
  /** The line under the name: their role, or why nobody is signed in. */
  accountNote?: string;
  /** Sign out. Without it, the footer offers a sign-in instead. */
  onSignOut?: () => void;
};

/** Masters are grouped by what they *are*, derived from their binding, so an
 * operator never has to organize the menu (decision #18). */
const GROUPS: { title: string; kinds: BindingKind[] }[] = [
  { title: "Structure", kinds: ["competition-node"] },
  { title: "People & teams", kinds: ["participant", "transfer"] },
  {
    title: "Reference",
    kinds: ["venue", "outcome-codes", "name-history", "media"],
  },
];

type Row = {
  href: string;
  label: string;
  icon: React.ReactNode;
  match?: "exact" | "prefix";
};

/**
 * The current sport's masters, kept in step with the URL.
 *
 * The shell stays mounted across navigation, so whatever the menu was given on
 * first load would otherwise never change: picking a sport from the switcher
 * left it showing the previous sport's masters, usually none. Each sport is
 * fetched once and remembered.
 *
 * This went through a `/api/nav/<sport>` route handler in this app, which
 * existed only because the admin API's host was server-only. It is a direct
 * call now — same data, one hop fewer.
 */
function useSportMasters(
  sport: string | undefined,
  initial: MasterNavItem[],
): MasterNavItem[] {
  // Seeded only when there is something to seed with.
  //
  // `initial` used to arrive complete, because a server component had already
  // awaited it. It is now the result of a fetch the layout starts at the same
  // moment this mounts, so on the first render it is an empty array — and
  // caching that emptiness was permanent: an empty array is truthy, so the
  // effect below saw a cached answer and never fetched. The sport menu stayed
  // blank while the page beside it listed all ten masters.
  const [bySport, setBySport] = useState<Record<string, MasterNavItem[]>>(() =>
    sport && initial.length > 0 ? { [sport]: initial } : {},
  );

  useEffect(() => {
    if (!sport || bySport[sport]) return;
    let live = true;
    void listMasters(sport)
      .then((masters) =>
        masters.map((m) => ({
          code: m.code,
          label: m.label,
          bindingKind: m.bindingKind,
        })),
      )
      .then((items) => {
        if (live) setBySport((previous) => ({ ...previous, [sport]: items }));
      })
      .catch(() => {
        // A sport with no published definition simply has no masters to show;
        // the shell must still render.
        if (live) setBySport((previous) => ({ ...previous, [sport]: [] }));
      });
    return () => {
      live = false;
    };
  }, [sport, bySport]);

  return sport ? (bySport[sport] ?? []) : [];
}

/**
 * Which groups the operator has closed.
 *
 * Subscribed rather than copied into state. `localStorage` *is* an external
 * store, and reading it into state in an effect both trips the cascading-render
 * rule and ignores the same menu open in another tab.
 */
function useCollapsed(sport: string | undefined): Collapsed {
  const scope = scopeOf(sport);
  const all = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return all[scope] ?? NONE;
}

function NavRow({ row, active }: { row: Row; active: boolean }) {
  return (
    <Link
      to={row.href}
      className={active ? `${styles.row} ${styles.rowActive}` : styles.row}
    >
      <span className={styles.rowIcon} aria-hidden>
        {row.icon}
      </span>
      <span className={styles.rowLabel}>{row.label}</span>
    </Link>
  );
}

function Group({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.group}>
      <button
        type="button"
        className={styles.groupHead}
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className={styles.groupTitle}>{title}</span>
        <span
          className={
            open ? styles.groupChevron : `${styles.groupChevron} ${styles.shut}`
          }
        >
          <IconChevronDown size={14} />
        </span>
      </button>
      {open && <div className={styles.groupRows}>{children}</div>}
    </div>
  );
}

export function Sidebar({
  sports,
  masters: initialMasters,
  rememberedSport,
  signedInAs,
  accountNote,
  onSignOut,
}: Props) {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Sport context: the URL's first segment (decision #17), or — while the old
  // masters screens still scope via ?sport= — the query param as a fallback.
  const firstSegment = pathname.split("/")[1] ?? "";
  const sportParam = searchParams.get("sport") ?? "";
  const urlSport = (
    sports.find((s) => s.code === firstSegment) ??
    sports.find((s) => s.code === sportParam)
  )?.code;

  // Plenty of pages are not sport-scoped — /sports, /fixtures, a fixture's own
  // detail. Deriving the sport from the URL alone dropped the whole sport menu
  // the moment you opened one of them, which reads as "you are no longer
  // working on this sport" when nothing of the sort happened. So the last sport
  // is remembered until "All sports" is chosen deliberately.
  const [picked, setPicked] = useState(rememberedSport ?? undefined);
  const [lastUrlSport, setLastUrlSport] = useState(urlSport);
  if (urlSport !== lastUrlSport) {
    // Adjusting state during render — the sanctioned pattern for reacting to a
    // changed input without an extra render pass.
    setLastUrlSport(urlSport);
    if (urlSport) setPicked(urlSport);
  }
  const currentSport = urlSport ?? picked;

  useEffect(() => {
    document.cookie = currentSport
      ? `${SPORT_COOKIE}=${encodeURIComponent(currentSport)}; path=/; max-age=${SPORT_COOKIE_MAX_AGE}; samesite=lax`
      : `${SPORT_COOKIE}=; path=/; max-age=0; samesite=lax`;
  }, [currentSport]);

  const masters = useSportMasters(currentSport, initialMasters);
  const collapsed = useCollapsed(currentSport);
  const scope = scopeOf(currentSport);

  // --- search --------------------------------------------------------------
  //
  // It filters this menu. Not a command palette: a box that looks like search
  // and only opens a different kind of thing is a worse lie than no box, and
  // seventeen rows across four groups is exactly the length at which typing
  // three letters beats reading.
  const [query, setQuery] = useState("");
  const search = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        search.current?.focus();
        search.current?.select();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const isActive = (row: Row) => isCurrent(pathname, row.href, row.match);

  // Everything the menu can show. Built once so the search can work over one flat
  // list rather than walking the tree separately.
  const { app, sportRows, grouped, all } = useMemo(() => {
    // What the tool does, regardless of sport.
    const app: Row[] = [
      // Dashboard first: it is the screen the app opens on and the one that holds
      // the sports list, so it is the way back rather than a sibling of Today.
      { href: "/dashboard", label: "Dashboard", icon: <IconGrid size={17} /> },
      { href: "/today", label: "Today", icon: <IconCalendar size={17} /> },
      // Every workflow, whatever it belongs to: one sport's scoring, every
      // sport's Results, an event's operations — so it is not a sport row.
      {
        href: "/workflows",
        label: "Workflows",
        icon: <IconNetwork size={17} />,
        match: "prefix",
      },
      // Scout data coming in, for every event: each one feeds a workflow's import.
      {
        href: "/integrations",
        label: "Integrations",
        icon: <Plug size={17} />,
        match: "prefix",
      },
    ];
    // What *this* sport does. A separate block rather than seven rows in one:
    // the gap is what says the switcher above changes these and not those, and
    // no heading is needed to say it.
    const sportRows: Row[] = currentSport
      ? [
          {
            href: `/${currentSport}`,
            label: "Overview",
            icon: <IconHome size={17} />,
          },
          {
            href: `/${currentSport}/competitions`,
            label: "Competitions",
            icon: <IconTrophy size={17} />,
          },
          {
            href: `/${currentSport}/fixtures`,
            label: "Fixtures",
            icon: <IconSwords size={17} />,
          },
          {
            href: `/${currentSport}/fixtures/new`,
            label: "Enter fixture",
            icon: <IconPlus size={17} />,
          },
          {
            href: `/${currentSport}/setup`,
            label: "Setup",
            icon: <IconSettings size={17} />,
          },
          // The during picture, and sport-scoped because a program is only ever
          // about one sport. Everything above this row is what exists before a
          // ball is bowled; a scoring program is what happens after.
          {
            href: `/${currentSport}/scoring`,
            label: "Scoring",
            icon: <IconZap size={17} />,
            match: "prefix",
          },
          // The after picture. Scoring says what a match records; stats say what
          // those records add up to — and it is its own row rather than a tab
          // inside scoring because a competition can own a catalogue the sport
          // does not.
          {
            href: `/${currentSport}/stats`,
            label: "Stats",
            icon: <IconChart size={17} />,
            match: "prefix",
          },
        ]
      : [
          {
            href: "/fixtures",
            label: "Fixtures",
            icon: <IconSwords size={17} />,
          },
        ];

    const grouped: { title: string; rows: Row[] }[] = [];
    if (currentSport) {
      for (const group of GROUPS) {
        const rows = masters
          .filter((m) => group.kinds.includes(m.bindingKind))
          .map((m) => ({
            href: `/${currentSport}/masters/${m.code}`,
            label: m.label,
            // Derived from what it is called, falling back to what it is bound
            // to — see `lib/masterIcon`. A master carries no icon of its own.
            icon: <Icon name={masterIcon(m)} size={17} />,
            match: "prefix" as const,
          }));
        if (rows.length > 0) grouped.push({ title: group.title, rows });
      }
    }
    grouped.push({
      title: "Authoring",
      rows: [
        // No "Sports" row: the dashboard already renders that list, and the
        // switcher's "All sports" goes to the same page. Three ways in was two
        // too many, and it was the row a sport-scoped menu needed least.
        {
          href: "/commons",
          label: "Commons",
          icon: <IconDatabase size={17} />,
        },
        // A Games belongs to no sport, so not a trophy: the same glyph as a
        // sport's own competitions would say they are the same kind of thing.
        {
          href: "/events",
          label: "Events",
          icon: <IconGlobe size={17} />,
          match: "prefix",
        },
        {
          href: "/roles",
          label: "Roles",
          icon: <IconTag size={17} />,
          match: "prefix",
        },
      ],
    });
    // Outbound delivery: who receives feeds, and the entitlement products.
    grouped.push({
      title: "Delivery",
      rows: [
        {
          href: "/delivery",
          label: "Clients",
          icon: <Send size={17} />,
          match: "prefix",
        },
        {
          href: "/delivery/packages",
          label: "Packages",
          icon: <Package size={17} />,
          match: "prefix",
        },
        {
          href: "/delivery/status",
          label: "Status",
          icon: <Activity size={17} />,
          match: "prefix",
        },
      ],
    });

    return {
      app,
      sportRows,
      grouped,
      all: [...app, ...sportRows, ...grouped.flatMap((g) => g.rows)],
    };
  }, [currentSport, masters]);

  const needle = query.trim().toLowerCase();
  const hits = needle
    ? all.filter((row) => row.label.toLowerCase().includes(needle))
    : [];

  return (
    <aside className={styles.sidebar}>
      {/* The switcher and the search carry the inset; the column itself has none,
          so the menu below can run the full width and put its scrollbar against
          the right edge instead of on top of the rows. */}
      <div className={styles.head}>
        <SportSwitcher
          sports={sports}
          current={currentSport}
          onPick={setPicked}
        />

        <div className={styles.searchWrap}>
          <span className={styles.searchIcon} aria-hidden>
            <IconSearch size={15} />
          </span>
          <input
            ref={search}
            className={styles.search}
            type="search"
            value={query}
            placeholder="Search menu…"
            aria-label="Search the menu"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setQuery("");
              // Enter goes to the first hit: having typed enough to see one
              // answer, reaching for the mouse to click it is the wasted step.
              if (e.key === "Enter" && hits.length > 0) {
                setQuery("");
                navigate(hits[0].href);
              }
            }}
          />
          {!query && <kbd className={styles.kbd}>⌘K</kbd>}
        </div>
      </div>

      <nav className={styles.nav}>
        {needle ? (
          <div className={styles.groupRows}>
            {hits.length === 0 ? (
              <p className={styles.hint}>
                Nothing in the menu matches “{query}”.
              </p>
            ) : (
              hits.map((row) => (
                <NavRow key={row.href} row={row} active={isActive(row)} />
              ))
            )}
          </div>
        ) : (
          <>
            <div className={styles.groupRows}>
              {app.map((row) => (
                <NavRow key={row.href} row={row} active={isActive(row)} />
              ))}
            </div>

            <div className={styles.groupRows}>
              {sportRows.map((row) => (
                <NavRow key={row.href} row={row} active={isActive(row)} />
              ))}
            </div>

            {grouped.map((group) => (
              <Group
                key={group.title}
                title={group.title}
                open={!collapsed.includes(group.title)}
                onToggle={() => toggle(scope, group.title)}
              >
                {group.rows.map((row) => (
                  <NavRow key={row.href} row={row} active={isActive(row)} />
                ))}
              </Group>
            ))}

            {currentSport && masters.length === 0 && (
              <p className={styles.hint}>
                {displayName(currentSport)} declares nothing to keep yet. Add it
                in setup and it appears here.
              </p>
            )}
          </>
        )}
      </nav>

      <div className={styles.footer}>
        <span className={styles.avatar} aria-hidden>
          {(signedInAs ?? "?").charAt(0).toUpperCase()}
        </span>
        <span className={styles.footerText}>
          <span className={styles.footerName}>
            {signedInAs ?? "Nobody signed in"}
          </span>
          {accountNote && (
            <span className={styles.footerNote}>{accountNote}</span>
          )}
        </span>
        {onSignOut ? (
          <button
            type="button"
            className={styles.footerAction}
            title="Sign out"
            aria-label="Sign out"
            onClick={onSignOut}
            style={{ border: 0, background: "none", cursor: "pointer" }}
          >
            <IconSignOut size={15} />
          </button>
        ) : (
          <Link
            to="/login"
            className={styles.footerAction}
            title="Sign in"
            aria-label="Sign in"
          >
            <IconUser size={15} />
          </Link>
        )}
      </div>
    </aside>
  );
}
